import React, { useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useQuery } from "@tanstack/react-query";
import { TbDeviceDesktopAnalytics } from "react-icons/tb";
import { Button, ButtonGroup } from "@heroui/react";
import { useTranslation } from "react-i18next";

import SimpleAreaChart from "./SimpleAreaChart";
import StackedBarsChart from "./StackedBarsChart";
import AreaBlackChart from "./AreaBlackChart";

interface GraphData {
  name: string;
  data: number[];
}

interface AnalyticsData {
  quantity: {
    graph: GraphData[];
    categories: string[];
  };
  assignment: {
    graph: GraphData[];
    categories: string[];
  };
  quality: {
    graph: GraphData[];
    categories: string[];
  };
  quantity_type: {
    graph: GraphData[];
    categories: string[];
  };
}

const getSeriesData = (graphData: GraphData[], name: string, seriesName: string) => {
  const data = graphData.find((item) => item.name === name)?.data || [];
  return [
    {
      name: seriesName,
      data: data,
    },
  ];
};

const mockStackedBarsCategories = (categories: string[]): string[] => {
  return categories.map((dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  });
};

const calculateAverage = (data: number[]): number => {
  if (data.length === 0) return 0;
  const total = data.reduce((acc, curr) => acc + curr, 0);
  return parseFloat((total / data.length).toFixed(2));
};

export default function Dashboard() {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [period, setPeriod] = useState<number>(30);
  const [stackType, setStackType] = useState<boolean>(true);

  const { data, error, isLoading } = useQuery<AnalyticsData, Error>({
    queryKey: ["analytics", period],
    queryFn: async () => {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - period);
      startDate.setHours(2, 0, 0, 0);
      const formattedDate = startDate.toISOString();

      const response = await axiosPrivate.get(`/analytics?min_end_datetime=${formattedDate}`);
      return response.data;
    },
  });

  const mapQualityNames = (graphDataQuality: GraphData[]) => {
    const nameMapping: { [key: string]: string } = {
      metadata_line_goodProposal_perc: t("valid_client_data"),
      product_line_goodProposal_perc: t("valid_product_data"),
      order_FirstTimeRight: t("order_forms_without_alerts"),
    };

    return graphDataQuality.map((item) => ({
      ...item,
      name: nameMapping[item.name] || item.name,
    }));
  };

  const mapTypeNames = (graphDataQuantityType: GraphData[]) => {
    const nameMapping: { [key: string]: string } = {
      status_success_sum: t("processed"),
      status_failed_sum: t("failed"),
      status_archive_sum: t("archived"),
    };

    return graphDataQuantityType.map((item) => ({
      ...item,
      name: nameMapping[item.name] || item.name,
    }));
  };

  if (isLoading) return <div>{t("loading")}</div>;
  if (error) return <div>{t("error")}: {error.message}</div>;

  const graphDataQuantity = data?.quantity.graph || [];
  const categoriesQuantity = data?.quantity.categories || [];

  const countOrdersData = graphDataQuantity.find((item) => item.name === "count_orders")?.data || [];
  const totalCountOrders = countOrdersData.reduce((acc, curr) => acc + curr, 0);

  const countLinesData = graphDataQuantity.find((item) => item.name === "count_lines")?.data || [];
  const totalCountLines = countLinesData.reduce((acc, curr) => acc + curr, 0);

  const graphDataAssignment = data?.assignment.graph || [];
  const categoriesAssignment = data?.assignment.categories || [];

  const countOrdersSeries = getSeriesData(graphDataQuantity, "count_orders", t("order_forms"));
  const countLinesSeries = getSeriesData(graphDataQuantity, "count_lines", t("lines"));

  const durationValidationData = graphDataQuantity.find((item) => item.name === "duration_validation_line")?.data || [];
  const truncatedDurationValidationData = durationValidationData.map((value) => Math.floor(value));
  const totalDurationValidation = truncatedDurationValidationData.reduce((acc, curr) => acc + curr, 0);
  const averageDurationValidation = truncatedDurationValidationData.length > 0
    ? totalDurationValidation / truncatedDurationValidationData.length
    : 0;

  const processDurationSeries = getSeriesData(
    [{ name: "duration_validation_line", data: truncatedDurationValidationData }],
    "duration_validation_line",
    t("processing_duration")
  );

  const graphDataQuality = mapQualityNames(data?.quality.graph || []);
  const categoriesQuality = data?.quality.categories || [];

  const metadataQualityAverage = calculateAverage(graphDataQuality.find((item) => item.name === t("valid_client_data"))?.data || []);
  const productQualityAverage = calculateAverage(graphDataQuality.find((item) => item.name === t("valid_product_data"))?.data || []);
  const orderQualityAverage = calculateAverage(graphDataQuality.find((item) => item.name === t("order_forms_without_alerts"))?.data || []);

  const graphDataQuantityType = data?.quantity_type.graph || [];
  const graphDataQuantityTypeMapped = mapTypeNames(graphDataQuantityType);
  const categoriesQuantityType = data?.quantity_type.categories || [];

  const statusSuccessSum = graphDataQuantityType.find((item) => item.name === "status_success_sum")?.data || [];
  const statusFailedSum = graphDataQuantityType.find((item) => item.name === "status_failed_sum")?.data || [];
  const statusArchiveSum = graphDataQuantityType.find((item) => item.name === "status_archive_sum")?.data || [];

  const totalSuccess = statusSuccessSum.reduce((acc, val) => acc + val, 0);
  const totalFailed = statusFailedSum.reduce((acc, val) => acc + val, 0);
  const totalArchive = statusArchiveSum.reduce((acc, val) => acc + val, 0);
  const total = totalSuccess + totalFailed + totalArchive;

  const orderSuccessAverage = ((totalSuccess / total) * 100).toFixed(2);
  const orderFailedAverage = ((totalFailed / total) * 100).toFixed(2);
  const orderArchiveAverage = ((totalArchive / total) * 100).toFixed(2);

  const getPeriodText = (period: number) => {
    switch (period) {
      case 7:
        return t("last_7_days");
      case 30:
        return t("last_30_days");
      case 300:
        return t("since_beginning");
      default:
        return "";
    }
  };

  if (isLoading) return <div>{t("loading")}</div>;
  if (error) {
    const errorMessage = (error as Error).message;
    return <div>{t("error")}: {errorMessage}</div>;
  }

  return (
    <div className="mx-10p">
      <div className="w-full text-vistagrey my-6 inline-flex justify-between items-center">
        <div>
          <div className="text-vistagrey text-xl">
            <TbDeviceDesktopAnalytics className=" fill-white inline-block text-4xl mr-2" />
            {t("dashboard")}
            <span className="text-xs rounded-xl bg-vistactared text-white font-bold ml-2 px-2 align-middle">
              {t("beta")}
            </span>
          </div>
          <div className="text-sm pl-11 -mt-2">{t("data_analysis")}</div>
        </div>
        <div className="text-vistagrey">
          <ButtonGroup>
            <Button
              className={`text-vistagrey ${period === 7 ? 'text-white bg-vistagreen' : ''}`}
              onClick={() => setPeriod(7)}
            >
              {t("last_7_days")}
            </Button>
            <Button
              className={`text-vistagrey ${period === 30 ? 'text-white bg-vistagreen' : ''}`}
              onClick={() => setPeriod(30)}
            >
              {t("last_30_days")}
            </Button>
            <Button
              className={`text-vistagrey ${period === 300 ? 'text-white bg-vistagreen' : ''}`}
              onClick={() => setPeriod(300)}
            >
              {t("since_beginning")}
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className="flex justify-between my-9 gap-20">
        <div className="w-96 flex flex-col flex-auto shadow bg-white rounded-t-2xl">
          <div className="flex justify-between mt-4 mx-5">
            <div className="text-sm ">{t("order_forms")}</div>
            <div className="bg-vistagrey/20 rounded-xl text-xs text-vistagrey font-bold px-2 pt-[2px]">
              {getPeriodText(period)}
            </div>
          </div>
          <div className="text-5xl font-bold tracking-tighter leading-tight mx-5">
            {totalCountOrders}
          </div>
          <SimpleAreaChart
            series={countOrdersSeries}
            height={120}
            categories={categoriesQuantity}
          />
        </div>
        <div className="w-96 flex flex-col flex-auto shadow bg-white rounded-t-2xl">
          <div className="flex justify-between mt-4 mx-5">
            <div className="text-sm ">{t("number_of_lines")}</div>
            <div className="bg-vistagrey/20 rounded-xl text-xs text-vistagrey font-bold px-2 pt-[2px]">
              {getPeriodText(period)}
            </div>
          </div>
          <div className="text-5xl font-bold tracking-tighter leading-tight mx-5">
            {totalCountLines}
          </div>
          <SimpleAreaChart
            series={countLinesSeries}
            height={120}
            categories={categoriesQuantity}
          />
        </div>
        <div className="w-96 flex flex-col flex-auto shadow bg-white rounded-t-2xl">
          <div className="flex justify-between mt-4 mx-5">
            <div className="text-sm ">{t("average_processing_per_line")}</div>
            <div className="bg-vistagrey/20 rounded-xl text-xs text-vistagrey font-bold px-2 pt-[2px]">
              {getPeriodText(period)}
            </div>
          </div>
          <div className="text-5xl font-bold tracking-tighter leading-tight mx-5">
            {Math.round(averageDurationValidation)} sec
          </div>
          <SimpleAreaChart
            series={processDurationSeries}
            height={120}
            categories={categoriesQuantity}
          />
        </div>
      </div>

      <div className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden bg-white">
        <div className="area-chart">
          <div className="text-vistagreen pt-5 px-8 flex">
            <div className="font-bold text-lg pr-5">
              {t("order_processing_distribution")}
            </div>
            <ButtonGroup size="sm">
              <Button
                className={`text-vistagrey ${stackType ? 'text-white bg-vistagreen' : ''}`}
                onClick={() => setStackType(true)}
              >
                {t("mode_percent")}
              </Button>
              <Button
                className={`text-vistagrey ${stackType ? 'text-white bg-vistagreen' : ''}`}
                onClick={() => setStackType(false)}
              >
                {t("mode_value")}
              </Button>
            </ButtonGroup>
          </div>
          <StackedBarsChart
            series={graphDataAssignment}
            height={stackType ? 350 : 650}
            categories={mockStackedBarsCategories(categoriesAssignment)}
            colors={["#A3673E", "#3F6163", "#3E9CA3", "#4E4138", "#2D3333"]}
            stackType={stackType}
          />
        </div>
      </div>

      <div className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden mt-9">
        <div className="bg-vistadark ">
          <div className="text-white pt-5 px-8">
            <div className="font-bold text-lg pb-2">{t("quality_indicators")}</div>
            <div className="flex gap-8 font-bold uppercase">
              <div className="text-xs text-[#029ddb]">
                {t("valid_client_data")} <br />
                <span className="text-2xl font-bold">
                  {metadataQualityAverage} %
                </span>
              </div>
              <div className="text-xs text-[#3e9ba2]">
                {t("valid_product_data")} <br />
                <span className="text-2xl font-bold">
                  {productQualityAverage} %
                </span>
              </div>
              <div className="text-xs text-[#99d98c]">
                {t("order_forms_without_alerts")} <br />
                <span className="text-2xl font-bold">
                  {orderQualityAverage} %
                </span>
              </div>
            </div>
          </div>
          <AreaBlackChart
            series={graphDataQuality}
            height={300}
            categories={categoriesQuality}
          />
        </div>
      </div>

      <div className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden bg-white mt-9">
        <div className="area-chart">
          <div className="text-vistagrey pt-5 px-8">
            <div className="font-bold text-lg pb-2">
              {t("order_forms_status")}
            </div>
            <div className="flex gap-8 font-bold uppercase">
              <div className="text-xs text-vistactagreenlight">
                {t("processed")} <br />
                <span className="text-2xl font-bold">
                  {orderSuccessAverage} %
                </span>{" "}
              </div>
              <div className="text-xs text-vistagrey">
                {t("archived")} <br />
                <span className="text-2xl font-bold">
                  {orderArchiveAverage} %
                </span>{" "}
              </div>
              <div className="text-xs text-vistactaredlight">
                {t("failed")} <br />
                <span className="text-2xl font-bold">
                  {orderFailedAverage} %
                </span>{" "}
              </div>
            </div>
          </div>
          <StackedBarsChart
            series={graphDataQuantityTypeMapped}
            height={350}
            categories={mockStackedBarsCategories(categoriesQuantityType)}
            colors={["#72ae35", "#d2302f", "#96a9b4"]}
          />
        </div>
      </div>
    </div>
  );
}